import {useCallback, useEffect, useMemo, useState} from "react";

/** Stable domain IDs and plain metadata, independent of the renderer or table library. */
export type ColumnDefinition = {
  id: string;
  title: string;
  columnLabel?: string;
  group?: string;
  defaultVisible?: boolean;
  width?: number;
  /** False for action/status columns and values without an unambiguous ordering. */
  sortable?: boolean;
};
export type ColumnState = {id: string; visible: boolean; width?: number};
export type ColumnSort = {id: string; direction: "asc" | "desc"};
export type ColumnLayout = {version: 1; columns: ColumnState[]; sort?: ColumnSort | null};
export type ColumnLayoutOptions = {defaultSort?: ColumnSort};
export type ColumnLayoutControls = {
  columns: (ColumnDefinition & {visible: boolean})[];
  setColumnVisible: (id: string, visible: boolean) => void;
  moveColumn: (id: string, targetId: string) => void;
  reset: () => void;
};

/** Reconcile untrusted storage with the current catalogue, including added/removed columns. */
export function resolveColumnLayout(
  columns: readonly ColumnDefinition[],
  saved: unknown,
  defaultSort?: ColumnSort
): ColumnLayout {
  const definitions = new Map(columns.map((column) => [column.id, column]));
  const result: ColumnState[] = [];
  const seen = new Set<string>();
  let sort: unknown = defaultSort;
  if (
    saved &&
    typeof saved === "object" &&
    "version" in saved &&
    saved.version === 1 &&
    "columns" in saved &&
    Array.isArray(saved.columns)
  ) {
    if ("sort" in saved) sort = saved.sort;
    for (const entry of saved.columns) {
      if (!entry || typeof entry.id !== "string" || seen.has(entry.id) || !definitions.has(entry.id)) continue;
      const definition = definitions.get(entry.id);
      if (!definition) continue;
      seen.add(entry.id);
      result.push({
        id: entry.id,
        visible: typeof entry.visible === "boolean" ? entry.visible : definition.defaultVisible !== false,
        width:
          typeof entry.width === "number" && Number.isFinite(entry.width) && entry.width >= 40 && entry.width <= 2000
            ? entry.width
            : undefined,
      });
    }
  }
  for (const column of columns) {
    if (!seen.has(column.id)) result.push({id: column.id, visible: column.defaultVisible !== false});
  }
  // Always leave a route back to the picker and a usable table.
  if (result.length && !result.some((column) => column.visible)) result[0].visible = true;
  const layout: ColumnLayout = {version: 1, columns: result};
  // Missing sort keeps old layouts compatible; null explicitly clears a default sort.
  if (sort !== undefined) {
    layout.sort =
      sort &&
      typeof sort === "object" &&
      "id" in sort &&
      typeof sort.id === "string" &&
      definitions.has(sort.id) &&
      definitions.get(sort.id)?.sortable !== false &&
      "direction" in sort &&
      (sort.direction === "asc" || sort.direction === "desc")
        ? {id: sort.id, direction: sort.direction}
        : null;
  }
  return layout;
}

function readLayout(key: string): unknown {
  try {
    return typeof window === "undefined" ? undefined : JSON.parse(window.localStorage.getItem(key) ?? "null");
  } catch {
    return undefined;
  }
}

export function useColumnLayout<C extends ColumnDefinition>(
  columns: readonly C[],
  storageKey: string,
  options?: ColumnLayoutOptions
) {
  const defaultSort = options?.defaultSort;
  const [stored, setStored] = useState(() => ({key: storageKey, value: readLayout(storageKey), dirty: false}));
  useEffect(() => {
    if (!stored.dirty) return;
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(stored.key, JSON.stringify(stored.value));
    } catch {
      // Private browsing, quota limits and disabled storage must not disable the controls.
    }
  }, [stored]);
  // Read the new scope immediately; never write the previous category's state into its key.
  const source = useMemo(
    () => (stored.key === storageKey ? stored.value : readLayout(storageKey)),
    [stored, storageKey]
  );
  const layout = useMemo(() => resolveColumnLayout(columns, source, defaultSort), [columns, source, defaultSort]);
  const definitions = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
  const orderedColumns = useMemo(
    () =>
      layout.columns.flatMap((state) => {
        const definition = definitions.get(state.id);
        return definition ? [{...definition, ...state, width: state.width ?? definition.width}] : [];
      }),
    [layout, definitions]
  );
  const visibleColumns = useMemo(() => orderedColumns.filter((column) => column.visible), [orderedColumns]);
  const sort = layout.sort ?? undefined;
  const activeSort = sort && visibleColumns.some((column) => column.id === sort.id) ? sort : undefined;

  const update = useCallback(
    (transform: (layout: ColumnLayout) => ColumnLayout) => {
      setStored((previous) => {
        const current = resolveColumnLayout(
          columns,
          previous.key === storageKey ? previous.value : readLayout(storageKey),
          defaultSort
        );
        const next = resolveColumnLayout(columns, transform(current), defaultSort);
        return {key: storageKey, value: next, dirty: true};
      });
    },
    [columns, storageKey, defaultSort]
  );

  const setColumnVisible = useCallback(
    (id: string, visible: boolean) =>
      update((current) => ({
        ...current,
        columns: current.columns.map((column) => (column.id === id ? {...column, visible} : column)),
      })),
    [update]
  );

  const moveColumn = useCallback(
    (id: string, targetId: string) =>
      update((current) => {
        const next = [...current.columns];
        const from = next.findIndex((column) => column.id === id);
        const to = next.findIndex((column) => column.id === targetId);
        if (from < 0 || to < 0 || from === to) return current;
        next.splice(to, 0, next.splice(from, 1)[0]);
        return {...current, columns: next};
      }),
    [update]
  );

  const onColumnMoved = useCallback(
    (from: number, to: number) => {
      if (visibleColumns[from] && visibleColumns[to]) moveColumn(visibleColumns[from].id, visibleColumns[to].id);
    },
    [visibleColumns, moveColumn]
  );

  const setColumnWidth = useCallback(
    (id: string, width: number) => {
      if (!Number.isFinite(width) || width < 40 || width > 2000) return;
      update((current) => ({
        ...current,
        columns: current.columns.map((state) => (state.id === id ? {...state, width} : state)),
      }));
    },
    [update]
  );

  const setSort = useCallback(
    (value: ColumnSort | undefined | null) => update((current) => ({...current, sort: value ?? null})),
    [update]
  );
  const toggleSort = useCallback(
    (id: string, initialDirection: ColumnSort["direction"] = "asc") =>
      update((current) => ({
        ...current,
        sort: {
          id,
          direction: current.sort?.id === id ? (current.sort.direction === "asc" ? "desc" : "asc") : initialDirection,
        },
      })),
    [update]
  );
  const reset = useCallback(
    () => update(() => resolveColumnLayout(columns, undefined, defaultSort)),
    [columns, update, defaultSort]
  );

  return {
    layout,
    columns: orderedColumns,
    visibleColumns,
    sort,
    activeSort,
    setSort,
    toggleSort,
    setColumnVisible,
    moveColumn,
    onColumnMoved,
    setColumnWidth,
    reset,
  };
}
